/**
 * NeoCore · Reportes contables INGECOP
 *
 * 3 reportes según formato oficial (PDF Carlitos):
 * 1. Estado de Resultados (rango de fechas)
 * 2. Estado de Situación Financiera / Balance General (fecha de corte)
 * 3. Libro Mayor (cuenta + rango)
 *
 * Formato visual: 6 columnas (NoCuenta · Nombre · DETALLE · PARCIAL · SUBTOTAL · TOTAL)
 * Estructura jerárquica: cada nivel de cuenta se pinta en su columna correspondiente.
 *
 * Export a PDF (task #143 aparte). Por ahora vista web + print CSS.
 */

import { useEffect, useState } from 'react'
import { fetchAll } from '@/lib/fetchAll'
import { supabase } from '@/lib/supabase'
import { Calendar, Printer, ChartLineUp, Scales, Books, FilePdf, MicrosoftExcelLogo } from '@phosphor-icons/react'
import { exportarPDF, exportarExcel } from '@/lib/exportReport'
import { fetchConfigReporte } from '@/lib/useConfigReporte'
import { hoyGT } from '../lib/fechas'

type Tab = 'estado_resultados' | 'balance' | 'libro_mayor'

// Formato INGECOP: cada cuenta trae su saldo consolidado + su nivel jerárquico.
// El saldo se pinta en la columna Nivel 5→1 que corresponde a char_length(codigo).
type FilaEF = {
  codigo: string
  nombre: string
  nivel: number       // = largo del código (1,2,3,4,6,8,10…)
  clase: string
  saldo: number
  tipo_fila: 'cuenta' | 'total' | 'resultado'
}

type FilaLM = {
  fecha: string
  numero_asiento: number
  descripcion: string
  origen: string
  tipo: string
  monto: number
  saldo_running: number
}

type CuentaOpt = { codigo: string; nombre: string }

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const hoy = () => hoyGT()
const inicioAno = () => `${hoyGT().slice(0, 4)}-01-01`

export function NeoCoreReportesContablesPage() {
  const [tab, setTab] = useState<Tab>('estado_resultados')

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 500 }}>Reportes contables</h1>
        <p style={{ marginTop: 4, color: 'var(--muted-foreground)', fontSize: 13 }}>
          Formato INGECOP · Estado de Resultados · Balance General · Libro Mayor
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        <TabBtn active={tab === 'estado_resultados'} onClick={() => setTab('estado_resultados')} icon={ChartLineUp}>
          Estado de Resultados
        </TabBtn>
        <TabBtn active={tab === 'balance'} onClick={() => setTab('balance')} icon={Scales}>
          Estado de Situación Financiera
        </TabBtn>
        <TabBtn active={tab === 'libro_mayor'} onClick={() => setTab('libro_mayor')} icon={Books}>
          Libro Mayor
        </TabBtn>
      </div>

      {tab === 'estado_resultados' && <EstadoResultadosView />}
      {tab === 'balance' && <BalanceGeneralView />}
      {tab === 'libro_mayor' && <LibroMayorView />}
    </div>
  )
}

function TabBtn({ active, onClick, icon: Icon, children }: { active: boolean; onClick: () => void; icon: any; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 14px', border: 'none',
        background: 'transparent',
        borderBottom: active ? '2px solid var(--brand-600, #FF5B04)' : '2px solid transparent',
        color: active ? 'var(--brand-600, #FF5B04)' : 'var(--ink-soft)',
        fontWeight: active ? 500 : 400, fontSize: 13, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      <Icon size={15} /> {children}
    </button>
  )
}

// ══════════════════════════════════════════════════════════════════════
// Estado de Resultados
// ══════════════════════════════════════════════════════════════════════

function EstadoResultadosView() {
  const [desde, setDesde] = useState(inicioAno())
  const [hasta, setHasta] = useState(hoy())
  const [rows, setRows] = useState<FilaEF[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cargar = async () => {
    setLoading(true); setError(null)
    const { data, error } = await supabase.rpc('reporte_estado_resultados', {
      p_fecha_desde: desde, p_fecha_hasta: hasta,
    })
    setLoading(false)
    if (error) setError(error.message)
    else setRows((data ?? []) as FilaEF[])
  }
  useEffect(() => { cargar() }, [])

  // Orden de presentación: productos · TOTAL PRODUCTOS · gastos · TOTAL GASTOS · RESULTADO
  const display = ordenarER(rows)

  const exportar = async (formato: 'pdf' | 'excel') => {
    const config = await fetchConfigReporte()
    const fn = formato === 'pdf' ? exportarPDF : exportarExcel
    await fn({
      config,
      titulo: 'ESTADO DE RESULTADOS',
      subtitulo: `Período del ${fecha_es(desde)} al ${fecha_es(hasta)}`,
      headers: HEADERS_EF,
      rows: display.map(filaExport),
      orientacion: 'portrait',
      columnasNumericas: [2, 3, 4, 5, 6],
      filename: 'estado-resultados',
    })
  }

  return (
    <div>
      <PeriodoBar
        desde={desde} hasta={hasta}
        onChange={(d, h) => { setDesde(d); setHasta(h) }}
        onGenerate={cargar} loading={loading}
        onPrint={() => window.print()}
        onExportPDF={() => exportar('pdf')}
        onExportExcel={() => exportar('excel')}
      />
      {error && <ErrorBox>{error}</ErrorBox>}
      <div className="report-print-area" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
        <ReportHeader
          titulo="ESTADO DE RESULTADOS"
          subtitulo={`Período del ${fecha_es(desde)} al ${fecha_es(hasta)}`}
        />
        <EstadoTable rows={display} loading={loading} />
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// Balance General
// ══════════════════════════════════════════════════════════════════════

function BalanceGeneralView() {
  const [corte, setCorte] = useState(hoy())
  const [rows, setRows] = useState<FilaEF[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cargar = async () => {
    setLoading(true); setError(null)
    const { data, error } = await supabase.rpc('reporte_balance_general', { p_fecha_corte: corte })
    setLoading(false)
    if (error) setError(error.message)
    else setRows((data ?? []) as FilaEF[])
  }
  useEffect(() => { cargar() }, [])

  // Orden: activo · TOTAL ACTIVO · pasivo · capital · RESULTADO · TOTAL PASIVO Y CAPITAL
  const display = ordenarBG(rows)

  const exportar = async (formato: 'pdf' | 'excel') => {
    const config = await fetchConfigReporte()
    const fn = formato === 'pdf' ? exportarPDF : exportarExcel
    await fn({
      config,
      titulo: 'ESTADO DE SITUACIÓN FINANCIERA',
      subtitulo: `Al ${fecha_es(corte)}`,
      headers: HEADERS_EF,
      rows: display.map(filaExport),
      orientacion: 'portrait',
      columnasNumericas: [2, 3, 4, 5, 6],
      filename: 'balance-general',
    })
  }

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
        padding: 12, background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8,
      }}>
        <Calendar size={16} color="var(--muted-foreground)" />
        <label style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Fecha de corte</label>
        <input type="date" value={corte} onChange={(e) => setCorte(e.target.value)} style={inputStyle} />
        <button onClick={cargar} disabled={loading} style={btnPrimary}>
          {loading ? 'Cargando…' : 'Generar'}
        </button>
        <button onClick={() => exportar('pdf')} style={btnGhost} title="Exportar PDF">
          <FilePdf size={13} /> PDF
        </button>
        <button onClick={() => exportar('excel')} style={btnGhost} title="Exportar Excel">
          <MicrosoftExcelLogo size={13} /> Excel
        </button>
        <button onClick={() => window.print()} style={btnGhost}>
          <Printer size={13} /> Imprimir
        </button>
      </div>
      {error && <ErrorBox>{error}</ErrorBox>}
      <div className="report-print-area" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
        <ReportHeader
          titulo="ESTADO DE SITUACIÓN FINANCIERA"
          subtitulo={`Al ${fecha_es(corte)}`}
        />
        <EstadoTable rows={display} loading={loading} />
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// Libro Mayor
// ══════════════════════════════════════════════════════════════════════

function LibroMayorView() {
  const [cuenta, setCuenta] = useState('')
  const [cuentas, setCuentas] = useState<CuentaOpt[]>([])
  const [desde, setDesde] = useState(inicioAno())
  const [hasta, setHasta] = useState(hoy())
  const [rows, setRows] = useState<FilaLM[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAll<CuentaOpt>((from, to) => supabase.from('cuenta_contable')
      .select('codigo, nombre')
      .eq('acepta_partidas', true)
      .eq('activa', true)
      .order('codigo')
      .range(from, to))
      .then(setCuentas)
      .catch(() => setCuentas([]))
  }, [])

  const cargar = async () => {
    if (!cuenta) return
    setLoading(true); setError(null)
    const { data, error } = await supabase.rpc('reporte_libro_mayor', {
      p_cuenta_codigo: cuenta, p_fecha_desde: desde, p_fecha_hasta: hasta,
    })
    setLoading(false)
    if (error) setError(error.message)
    else setRows((data ?? []) as FilaLM[])
  }

  const cuentaInfo = cuentas.find(c => c.codigo === cuenta)

  const exportar = async (formato: 'pdf' | 'excel') => {
    if (!cuentaInfo) return
    const config = await fetchConfigReporte()
    const headers = ['Fecha', 'No.', 'Descripción', 'Tipo', 'Monto', 'Saldo']
    const filas: (string | number)[][] = rows.map(r => [
      r.fecha,
      r.numero_asiento || '',
      r.descripcion,
      r.tipo,
      r.monto > 0 ? fmt(r.monto) : '',
      `Q ${fmt(r.saldo_running)}`,
    ])
    const fn = formato === 'pdf' ? exportarPDF : exportarExcel
    await fn({
      config,
      titulo: 'LIBRO MAYOR',
      subtitulo: `Cuenta ${cuentaInfo.codigo} · ${cuentaInfo.nombre} · del ${fecha_es(desde)} al ${fecha_es(hasta)}`,
      headers, rows: filas,
      orientacion: 'landscape',
      columnasNumericas: [4, 5],
      filename: `libro-mayor-${cuentaInfo.codigo}`,
    })
  }

  return (
    <div>
      <div style={{
        display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto auto auto auto', gap: 8, marginBottom: 16,
        padding: 12, background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8,
      }}>
        <select value={cuenta} onChange={(e) => setCuenta(e.target.value)} style={inputStyle}>
          <option value="">Seleccionar cuenta…</option>
          {cuentas.map((c) => (
            <option key={c.codigo} value={c.codigo}>{c.codigo} · {c.nombre}</option>
          ))}
        </select>
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={inputStyle} />
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={inputStyle} />
        <button onClick={cargar} disabled={loading || !cuenta} style={btnPrimary}>
          {loading ? 'Cargando…' : 'Generar'}
        </button>
        <button onClick={() => exportar('pdf')} disabled={!cuenta || rows.length === 0} style={btnGhost} title="Exportar PDF">
          <FilePdf size={13} /> PDF
        </button>
        <button onClick={() => exportar('excel')} disabled={!cuenta || rows.length === 0} style={btnGhost} title="Exportar Excel">
          <MicrosoftExcelLogo size={13} /> Excel
        </button>
        <button onClick={() => window.print()} style={btnGhost}>
          <Printer size={13} /> Imprimir
        </button>
      </div>
      {error && <ErrorBox>{error}</ErrorBox>}
      {cuentaInfo && (
        <div className="report-print-area" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
          <ReportHeader
            titulo="LIBRO MAYOR"
            subtitulo={`Cuenta ${cuentaInfo.codigo} · ${cuentaInfo.nombre} · del ${fecha_es(desde)} al ${fecha_es(hasta)}`}
          />
          <table style={reportTableStyle}>
            <thead>
              <tr>
                <th style={{ ...thReport, width: 90 }}>Fecha</th>
                <th style={{ ...thReport, width: 60 }}>No.</th>
                <th style={{ ...thReport, textAlign: 'left' }}>Descripción</th>
                <th style={{ ...thReport, width: 70 }}>Tipo</th>
                <th style={{ ...thReport, textAlign: 'right', width: 100 }}>Monto</th>
                <th style={{ ...thReport, textAlign: 'right', width: 120 }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ background: r.origen === 'SALDO_INICIAL' ? '#fef3c7' : undefined }}>
                  <td style={tdReport}>{r.fecha}</td>
                  <td style={{ ...tdReport, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.numero_asiento || ''}</td>
                  <td style={tdReport}>{r.descripcion}</td>
                  <td style={tdReport}>{r.tipo}</td>
                  <td style={{ ...tdReport, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {r.monto > 0 ? fmt(r.monto) : ''}
                  </td>
                  <td style={{ ...tdReport, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                    Q {fmt(r.saldo_running)}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && cuenta && !loading && (
                <tr><td colSpan={6} style={{ ...tdReport, textAlign: 'center', color: 'var(--muted-foreground)', padding: 24 }}>
                  Sin movimientos en el rango seleccionado
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// Componentes compartidos
// ══════════════════════════════════════════════════════════════════════

function PeriodoBar({ desde, hasta, onChange, onGenerate, loading, onPrint, onExportPDF, onExportExcel }: {
  desde: string; hasta: string; onChange: (d: string, h: string) => void
  onGenerate: () => void; loading: boolean; onPrint: () => void
  onExportPDF?: () => void; onExportExcel?: () => void
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
      padding: 12, background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8,
      flexWrap: 'wrap',
    }}>
      <Calendar size={16} color="var(--muted-foreground)" />
      <label style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Desde</label>
      <input type="date" value={desde} onChange={(e) => onChange(e.target.value, hasta)} style={inputStyle} />
      <label style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Hasta</label>
      <input type="date" value={hasta} onChange={(e) => onChange(desde, e.target.value)} style={inputStyle} />
      <button onClick={onGenerate} disabled={loading} style={btnPrimary}>
        {loading ? 'Cargando…' : 'Generar'}
      </button>
      {onExportPDF && (
        <button onClick={onExportPDF} style={btnGhost} title="Exportar PDF">
          <FilePdf size={13} /> PDF
        </button>
      )}
      {onExportExcel && (
        <button onClick={onExportExcel} style={btnGhost} title="Exportar Excel">
          <MicrosoftExcelLogo size={13} /> Excel
        </button>
      )}
      <button onClick={onPrint} style={btnGhost}>
        <Printer size={13} /> Imprimir
      </button>
    </div>
  )
}

function ReportHeader({ titulo, subtitulo }: { titulo: string; subtitulo: string }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid var(--ink)' }}>
      <div style={{ fontWeight: 500, fontSize: 15 }}>NEOCOOP, R.L.</div>
      <div style={{ fontWeight: 500, fontSize: 14, marginTop: 4 }}>{titulo}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 2 }}>{subtitulo}</div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// Tabla de estados financieros · formato INGECOP (5 columnas por nivel)
// ══════════════════════════════════════════════════════════════════════

// Columnas de monto, en orden de despliegue (izq → der)
const NIVELES_COL = [5, 4, 3, 2, 1] as const
const HEADERS_EF = ['Código', 'Nombre de la cuenta', 'Nivel 5', 'Nivel 4', 'Nivel 3', 'Nivel 2', 'Nivel 1']

// char_length del código → columna Nivel INGECOP (1=clase … 5=subcuenta/detalle)
function nivelCol(nivel: number): number {
  if (nivel <= 1) return 1
  if (nivel <= 3) return 2
  if (nivel <= 5) return 3
  if (nivel <= 7) return 4
  return 5
}

// Balance General: activo · TOTAL ACTIVO · pasivo · capital · RESULTADO · TOTAL P+C
function ordenarBG(rows: FilaEF[]): FilaEF[] {
  const c = rows.filter(r => r.tipo_fila === 'cuenta')
  const totAct = rows.find(r => r.codigo === 'TOT_ACT')
  const res = rows.find(r => r.codigo === 'RES_EJ')
  const totPC = rows.find(r => r.codigo === 'TOT_PC')
  const out: FilaEF[] = []
  out.push(...c.filter(r => r.clase === '1' || r.clase === '2'))
  if (totAct) out.push(totAct)
  out.push(...c.filter(r => r.clase === '3' || r.clase === '4'))
  out.push(...c.filter(r => r.clase === '5'))
  if (res) out.push(res)
  if (totPC) out.push(totPC)
  return out
}

// Estado de Resultados: productos · TOTAL PRODUCTOS · gastos · TOTAL GASTOS · RESULTADO
function ordenarER(rows: FilaEF[]): FilaEF[] {
  const c = rows.filter(r => r.tipo_fila === 'cuenta')
  const totProd = rows.find(r => r.codigo === 'TOT_PROD')
  const totGast = rows.find(r => r.codigo === 'TOT_GAST')
  const res = rows.find(r => r.codigo === 'RES_EJ')
  const out: FilaEF[] = []
  out.push(...c.filter(r => r.clase === '6'))
  if (totProd) out.push(totProd)
  out.push(...c.filter(r => ['7', '8', '9'].includes(r.clase)))
  if (totGast) out.push(totGast)
  if (res) out.push(res)
  return out
}

// Fila para export (Excel/PDF): monto en la columna de su nivel, resto vacío
function filaExport(r: FilaEF): (string | number)[] {
  const esTotal = r.tipo_fila !== 'cuenta'
  const col = esTotal ? 1 : nivelCol(r.nivel)
  const sangria = esTotal ? 0 : Math.max(0, col - 1)
  const montos = NIVELES_COL.map(n => (col === n ? fmt(r.saldo) : ''))
  return [esTotal ? '' : r.codigo, '   '.repeat(sangria) + r.nombre, ...montos]
}

function EstadoTable({ rows, loading }: { rows: FilaEF[]; loading: boolean }) {
  return (
    <table style={reportTableStyle}>
      <thead>
        <tr>
          <th style={{ ...thReport, width: 96, textAlign: 'left' }}>CÓDIGO</th>
          <th style={{ ...thReport, textAlign: 'left' }}>NOMBRE DE LA CUENTA</th>
          {NIVELES_COL.map(n => (
            <th key={n} style={{ ...thReport, textAlign: 'right', width: 96 }}>Nivel {n}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => <FilaEFRow key={`${r.codigo}-${i}`} r={r} />)}
        {rows.length === 0 && (
          <tr><td colSpan={7} style={{ ...tdReport, textAlign: 'center', color: 'var(--muted-foreground)', padding: 24 }}>
            {loading ? 'Cargando…' : 'Sin cuentas con saldo en el período seleccionado'}
          </td></tr>
        )}
      </tbody>
    </table>
  )
}

function FilaEFRow({ r }: { r: FilaEF }) {
  const esTotal = r.tipo_fila === 'total'
  const esResultado = r.tipo_fila === 'resultado'
  const esEspecial = esTotal || esResultado
  const col = esEspecial ? 1 : nivelCol(r.nivel)
  const isClase = !esEspecial && r.nivel === 1
  const bold = esEspecial || col <= 4            // agregados en negrita; hojas (col 5) normal
  const indent = 6 + (col - 1) * 14
  const bg = esTotal ? '#fff5ed'
    : esResultado ? '#fef3c7'
    : isClase ? '#e5e7eb'
    : col <= 3 ? '#f9fafb'
    : undefined
  const rojo = r.saldo < 0

  return (
    <tr style={{ background: bg, borderTop: esTotal ? '2px solid #FF5B04' : undefined }}>
      <td style={{ ...tdReport, fontFamily: 'monospace', fontSize: 10.5 }}>{esEspecial ? '' : r.codigo}</td>
      <td style={{
        ...tdReport, paddingLeft: indent, fontWeight: bold ? 600 : 400,
        textTransform: isClase || esEspecial ? 'uppercase' : 'none',
      }}>
        {r.nombre}
      </td>
      {NIVELES_COL.map(n => (
        <td key={n} style={{
          ...tdReport, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
          fontWeight: bold ? 600 : 400, color: rojo ? '#B91C1C' : undefined,
        }}>
          {col === n ? fmt(r.saldo) : ''}
        </td>
      ))}
    </tr>
  )
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
      {children}
    </div>
  )
}

const MES_ES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre']
function fecha_es(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${parseInt(d, 10)} de ${MES_ES[parseInt(m, 10) - 1]} de ${y}`
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
  fontSize: 13, background: 'var(--surface, #fff)', fontFamily: 'inherit',
}
const btnPrimary: React.CSSProperties = {
  padding: '8px 16px', border: 'none', borderRadius: 6,
  background: 'var(--brand-600, #FF5B04)', color: '#fff',
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
}
const btnGhost: React.CSSProperties = {
  padding: '8px 14px', border: '1px solid var(--border)', borderRadius: 6,
  background: 'var(--surface, #fff)', fontSize: 13, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
}
const reportTableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: 12,
  border: '1px solid var(--ink)',
}
const thReport: React.CSSProperties = {
  padding: '6px 8px', textAlign: 'center', fontWeight: 500, fontSize: 11,
  background: '#e5e7eb', border: '1px solid var(--ink)',
}
const tdReport: React.CSSProperties = {
  padding: '4px 8px', border: '1px solid var(--ink)', fontSize: 11.5,
}
